// =====================================================================
// Assessment Center — Auth module: users repository.
//
// In-memory now; every function is shaped like the D1 query it will
// become (an INSERT, a SELECT ... WHERE email = ?). Mirrors the
// `users` / `teachers` / `students` tables from
// migrations/0001_initial_schema.sql exactly — no schema changes were
// needed for auth, since the schema already modeled a role-bearing users
// table with 1:1 teacher/student extensions.
//
// Seeds the two demo accounts used throughout this project's sample
// data (student_1 / teacher_1), so logging in as either immediately
// shows the pre-populated exams, questions, and grading queue. See
// README.md for the demo credentials.
// =====================================================================
import type { User, Teacher, Student, UserRole } from "../types/db";

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const nowIso = () => new Date().toISOString();

let users: User[] = [];
let teachers: Teacher[] = [];
let students: Student[] = [];

export function findUserByEmail(email: string, role: UserRole): User | undefined {
  const normalized = email.trim().toLowerCase();
  return users.find((u) => u.email.toLowerCase() === normalized && u.role === role);
}

export function getUser(id: string): User | undefined {
  return users.find((u) => u.id === id);
}

export function getTeacher(userId: string): Teacher | undefined {
  return teachers.find((t) => t.user_id === userId);
}

export function getStudent(userId: string): Student | undefined {
  return students.find((s) => s.user_id === userId);
}

export interface CreateUserInput {
  email: string;
  password_hash: string;
  full_name: string;
  role: UserRole;
}

export function createUser(input: CreateUserInput): User {
  const user: User = {
    id: uuid(),
    email: input.email.trim(),
    password_hash: input.password_hash,
    full_name: input.full_name.trim(),
    role: input.role,
    status: "active",
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  users.push(user);
  return user;
}

export function createTeacherProfile(userId: string, institution?: string | null): Teacher {
  const teacher: Teacher = {
    user_id: userId,
    bio: null,
    specialization: institution ?? null,
    hire_date: null,
    created_at: nowIso(),
  };
  teachers.push(teacher);
  return teacher;
}

export function createStudentProfile(userId: string): Student {
  const student: Student = {
    user_id: userId,
    student_code: null,
    enrollment_date: nowIso(),
    created_at: nowIso(),
  };
  students.push(student);
  return student;
}

export function updateUserName(userId: string, fullName: string): void {
  const idx = users.findIndex((u) => u.id === userId);
  if (idx === -1) return;
  users[idx] = { ...users[idx], full_name: fullName.trim(), updated_at: nowIso() };
}

// ---------------------------------------------------------------------
// Demo seed — same IDs used by src/shared/grading/attempts-store.ts
// and the instructor exam/question-bank mock data, so these two demo
// accounts see the pre-populated content immediately.
// Passwords: "student-demo" / "instructor-demo" (see README.md).
// ---------------------------------------------------------------------
import { hashPassword } from "./password";

(async function seedDemoUsers() {
  const studentHash = await hashPassword("student-demo");
  const instructorHash = await hashPassword("instructor-demo");

  users.push(
    {
      id: "student_1",
      email: "demo.student@aviationhub.dev",
      password_hash: studentHash,
      full_name: "Alex Rivera",
      role: "student",
      status: "active",
      created_at: nowIso(),
      updated_at: nowIso(),
    },
    {
      id: "teacher_1",
      email: "demo.instructor@aviationhub.dev",
      password_hash: instructorHash,
      full_name: "Dr. Sam Whitfield",
      role: "teacher",
      status: "active",
      created_at: nowIso(),
      updated_at: nowIso(),
    }
  );
  students.push({ user_id: "student_1", student_code: "STU-1001", enrollment_date: nowIso(), created_at: nowIso() });
  teachers.push({ user_id: "teacher_1", bio: null, specialization: "Air Law & Meteorology", hire_date: null, created_at: nowIso() });
})();
